import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Badge,
  Typography,
  Container,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  Button,
  Divider,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';

// Icons
import HomeIcon from '@mui/icons-material/Home';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import EventIcon from '@mui/icons-material/Event';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpIcon from '@mui/icons-material/Help';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import SecurityIcon from '@mui/icons-material/Security';
import ShopIcon from '@mui/icons-material/Store';
import ReportIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import SupportIcon from '@mui/icons-material/Support';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

// Logo
import logo0 from '../assets/logo0.png';
import searchIcon from '../assets/search.png';
import chatIcon from '../assets/chat.png';

const NavbarContainer = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#000000',
  boxShadow: 'none',
  position: 'sticky',
  top: 0,
  zIndex: 1100,
}));

const NavToolbar = styled(Toolbar)({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0 16px',
  minHeight: '56px',
});

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const Logo = styled('img')({
  height: '32px',
  marginRight: '8px',
  filter: 'brightness(0) invert(1)', // Makes dark logo visible on dark background
});

const NavSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const NavLink = styled(Link)(({ theme }) => ({
  color: '#FFFFFF',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0 12px',
  textDecoration: 'none',
  fontSize: '0.75rem',
  '&:hover': {
    color: '#FFE302',
  },
}));

const IconContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const BadgeStyled = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#FFE302',
    color: '#000000',
  },
}));

const MobileMenuButton = styled(IconButton)({
  color: '#fff',
  display: 'none',
  '@media (max-width: 900px)': {
    display: 'block',
  }
});

const SocialIconButton = styled(IconButton)({
  color: '#fff',
  padding: '8px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#FFE302',
  }
});

const SearchInput = styled(TextField)({
  backgroundColor: '#FFFFFF',
  borderRadius: '0',
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    '& fieldset': {
      border: 'none',
    },
  },
});

const SearchButton = styled(Button)({
  backgroundColor: '#333',
  color: '#fff',
  height: '40px',
  borderRadius: '0',
  padding: '0 24px',
  '&:hover': {
    backgroundColor: '#444',
  },
});

const DomainButton = styled(Button)({
  backgroundColor: '#FFFFFF',
  color: '#00693C',
  height: '40px',
  minWidth: '48px',
  padding: '0 12px',
  borderRadius: '0',
  fontWeight: 600,
});

const TopNavLinks = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginLeft: 'auto',
  '@media (max-width: 900px)': {
    display: 'none',
  },
});

const TopNavLink = styled(Link)({
  color: '#FFFFFF',
  textDecoration: 'none',
  fontSize: '14px',
  fontWeight: 500,
  '&:hover': {
    color: '#FFE302',
  },
});

const Navbar1 = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const open = Boolean(profileAnchorEl);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleProfileClose();
    navigate(path);
  };

  const handleChatClick = () => {
    navigate('/chat');
  };

  const handleAskQuestion = () => {
    navigate('/question');
  };

  // Use the existing categories from the original Navbar1
  const categories = [
    { name: 'AI DETECT', path: '/ai-detect', icon: <SecurityIcon /> },
    { name: 'SHOP', path: '/marketplace', icon: <ShopIcon /> },
    { name: 'REPORT', path: '/reports', icon: <ReportIcon /> },
    { name: 'REMOTE SUPPORT', path: '/terms', icon: <SupportIcon /> },
    { name: 'SCANS HISTORY', path: '/scanhistory', icon: <HistoryIcon /> }
  ];

  // Additional navigation items for the right side
  const rightNavItems = [
    { name: 'Messages', icon: <EmailIcon />, path: '/messages', badge: 2 },
    { name: 'Notifications', icon: <NotificationsIcon />, path: '/notifications', badge: 3 },
    { name: 'Help', icon: <HelpIcon />, path: '/help' },
  ];

  // Social media links
  const socialLinks = [
    { name: 'Twitter', icon: <TwitterIcon />, url: 'https://twitter.com' },
    { name: 'Facebook', icon: <FacebookIcon />, url: 'https://facebook.com' },
    { name: 'Instagram', icon: <InstagramIcon />, url: 'https://instagram.com' },
    { name: 'LinkedIn', icon: <LinkedInIcon />, url: 'https://linkedin.com' }
  ];

  // Top navigation links from screenshot
  const topNavLinks = [
    { name: 'AI DETECT', path: '/ai-detect', icon: <VerifiedUserIcon /> },
    { name: 'MARKETPLACE', path: '/shop', icon: <ShopIcon /> },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Questions', path: '/questions' },
    { name: 'Consultation', path: '/consultation' }
  ];

  return (
    <>
      <NavbarContainer>
        <NavToolbar>
          <LogoContainer>
            <MobileMenuButton onClick={handleDrawerToggle}>
              <MenuIcon />
            </MobileMenuButton>
            <NavLink to="/dashboard">
              <Logo src={logo0} alt="Logo" />
            </NavLink>
            
            {/* Chat Icon - Moved closer to logo in PC version */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 2 }}>
              <NavLink to="/feed2">
                <IconContainer>
                  <img 
                    src={chatIcon} 
                    alt="Chat" 
                    style={{ 
                      width: '24px', 
                      height: '24px',
                      filter: 'brightness(0) invert(1)', // This makes black icons white
                    }} 
                  />
                </IconContainer>
              </NavLink>
            </Box>
          </LogoContainer>

          {/* Chat Icon - Only for mobile */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <NavLink to="/feed2">
              <IconContainer>
                <img 
                  src={chatIcon} 
                  alt="Chat" 
                  style={{ 
                    width: '28px', 
                    height: '28px',
                    filter: 'brightness(0) invert(1)', // This makes black icons white
                  }} 
                />
              </IconContainer>
            </NavLink>
          </Box>
          
          {/* Search Icon */}
          <Box sx={{ display: 'flex', mr: 2 }}>
            <NavLink to="/smart-search">
              <IconContainer>
                <img 
                  src={searchIcon} 
                  alt="Search" 
                  style={{ 
                    width: '24px', 
                    height: '24px',
                    filter: 'brightness(0) invert(1)', // This makes black icons white
                  }} 
                />
              </IconContainer>
            </NavLink>
          </Box>

          {!isMobile && (
            <TopNavLinks>
              {topNavLinks.map((link) => (
                <TopNavLink key={link.name} to={link.path} sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  ...(link.name === 'AI DETECT' || link.name === 'MARKETPLACE' ? {
                    fontWeight: 'bold',
                    color: '#FFE302'
                  } : {})
                }}>
                  {link.icon && <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>{link.icon}</Box>}
                  {link.name}
                </TopNavLink>
              ))}
            </TopNavLinks>
          )}

          <NavSection>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Notification Icons */}
              {rightNavItems.filter(item => ['Messages', 'Notifications'].includes(item.name)).map((item) => (
                <Tooltip title={item.name} key={item.name}>
                  <IconButton 
                    color="inherit" 
                    size="small"
                    component={Link}
                    to={item.path}
                  >
                    {item.badge ? (
                      <BadgeStyled 
                        badgeContent={item.badge} 
                        color="secondary"
                        sx={{ '& .MuiBadge-badge': { backgroundColor: '#00693C', color: '#fff' } }}
                      >
                        {item.icon}
                      </BadgeStyled>
                    ) : (
                      item.icon
                    )}
                  </IconButton>
                </Tooltip>
              ))}

              {!isSmallScreen && (
                <Box sx={{ display: 'flex', ml: 1 }}>
                  {socialLinks.slice(0, 2).map((item) => (
                    <Tooltip title={item.name} key={item.name}>
                      <SocialIconButton 
                        component="a" 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        size="small"
                      >
                        {item.icon}
                      </SocialIconButton>
                    </Tooltip>
                  ))}
                </Box>
              )}
              
              {/* Ask Question Button - Positioned before profile icon */}
              {!isMobile && (
                <Button
                  variant="contained"
                  startIcon={<QuestionAnswerIcon />}
                  onClick={handleAskQuestion}
                  sx={{
                    backgroundColor: '#FFE302',
                    color: '#000000',
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                    padding: '6px 15px',
                    '&:hover': {
                      backgroundColor: '#FFD700',
                    },
                    mr: 1
                  }}
                >
                  Ask Question
                </Button>
              )}
              
              <Tooltip title="Profile">
                <IconButton 
                  color="inherit" 
                  sx={{ p: 1 }}
                  onClick={handleProfileClick}
                  aria-controls={open ? 'profile-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            {/* Profile Dropdown Menu */}
            <Menu
              id="profile-menu"
              anchorEl={profileAnchorEl}
              open={open}
              onClose={handleProfileClose}
              onClick={handleProfileClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={() => handleMenuItemClick('/profile')}>
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/settings')}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => handleMenuItemClick('/logout')}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </NavSection>
        </NavToolbar>
      </NavbarContainer>

      {/* Dynamic Forms Section with White Forms */}
      <Box sx={{ 
        backgroundColor: '#00693C', 
        padding: '12px 0',
        borderTop: '1px solid #005030',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <Container maxWidth="lg">
          {/* White Dynamic Forms */}
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 2
          }}>
            {/* Form 1 */}
            <Box sx={{ 
              flex: 1, 
              backgroundColor: '#fff',
              borderRadius: 1,
              padding: '10px 15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                Website Security Check
              </Typography>
              <Button 
                variant="contained"
                size="small"
                sx={{ 
                  backgroundColor: '#000', 
                  color: '#fff',
                  '&:hover': { backgroundColor: '#333' }
                }}
              >
                Scan
              </Button>
            </Box>
            
            {/* Form 2 */}
            <Box sx={{ 
              flex: 1, 
              backgroundColor: '#fff',
              borderRadius: 1,
              padding: '10px 15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                Device Diagnostics
              </Typography>
              <Button 
                variant="contained"
                size="small"
                sx={{ 
                  backgroundColor: '#000', 
                  color: '#fff',
                  '&:hover': { backgroundColor: '#333' }
                }}
              >
                Check
              </Button>
            </Box>
            
            {/* Form 3 */}
            <Box sx={{ 
              flex: 1, 
              backgroundColor: '#fff',
              borderRadius: 1,
              padding: '10px 15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                Network Analysis
              </Typography>
              <Button 
                variant="contained"
                size="small"
                sx={{ 
                  backgroundColor: '#000', 
                  color: '#fff',
                  '&:hover': { backgroundColor: '#333' }
                }}
              >
                Analyze
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: 1, md: 3 },
            flexWrap: 'wrap'
          }}>
            {/* REPORT Button */}
            <Button
              component={Link}
              to="/report"
              startIcon={<ReportIcon />}
              sx={{
                backgroundColor: 'rgba(0,0,0,0.3)',
                color: '#fff',
                borderRadius: 0,
                padding: '6px 12px',
                fontSize: '0.8rem',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.5)',
                }
              }}
            >
              REPORT
            </Button>

            {/* REMOTE SUPPORT Button */}
            <Button
              component={Link}
              to="/remotesupport"
              startIcon={<SupportIcon />}
              sx={{
                backgroundColor: 'rgba(0,0,0,0.3)',
                color: '#fff',
                borderRadius: 0,
                padding: '6px 12px',
                fontSize: '0.8rem',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.5)',
                }
              }}
            >
              REMOTE SUPPORT
            </Button>

            {/* SCANS HISTORY Button */}
            <Button
              component={Link}
              to="/scanhistory"
              startIcon={<HistoryIcon />}
              sx={{
                backgroundColor: 'rgba(0,0,0,0.3)',
                color: '#fff',
                borderRadius: 0,
                padding: '6px 12px',
                fontSize: '0.8rem',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.5)',
                }
              }}
            >
              SCANS HISTORY
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': { 
            width: '280px',
            backgroundColor: '#111'
          },
        }}
      >
        <Box sx={{ p: 2, color: 'white' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Link to="/dashboard">
              <Logo src={logo0} alt="Logo" />
            </Link>
            <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', my: 1 }} />
          
          <List>
            {topNavLinks.map((link) => (
              <ListItem key={link.name} sx={{ p: 0, mb: 1 }}>
                <Button
                  component={Link}
                  to={link.path}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  {link.name}
                </Button>
              </ListItem>
            ))}
            
            {/* Ask Question Button in Mobile Menu */}
            <ListItem sx={{ p: 0, mb: 1 }}>
              <Button
                onClick={handleAskQuestion}
                fullWidth
                startIcon={<QuestionAnswerIcon />}
                sx={{
                  justifyContent: 'flex-start',
                  backgroundColor: '#FFE302',
                  color: '#000',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#FFD700',
                  }
                }}
              >
                Ask Question
              </Button>
            </ListItem>
          </List>
          
          <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', my: 1 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            {socialLinks.map((item) => (
              <SocialIconButton 
                key={item.name}
                component="a" 
                href={item.url} 
                target="_blank" 
                rel="noopener noreferrer"
                size="small"
              >
                {item.icon}
              </SocialIconButton>
            ))}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar1;
